import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import {
  Database,
  equalTo,
  getDatabase,
  onValue,
  orderByChild,
  query,
  ref,
  Unsubscribe,
} from 'firebase/database';
import { Observable } from 'rxjs';
import { FirebaseAppService } from './firebase-app.service';
import { AuthService } from 'app/features/auth/data-access/auth.service';

declare const ngDevMode: boolean | undefined;

export type NotificationType = 'info' | 'warning' | 'success' | 'error';

export interface NotificationItem {
  id: string;
  data: {
    body: string;
    eventId: string;
    eventtype: string;
    isRead: boolean;
    timestamp: number;
    title: string;
  };
  recipient: {
    role: string;
    userId: string;
  };
}

@Injectable({ providedIn: 'root' })
export class FirebaseRealtimeDatabaseService {
  private database?: Database;

  constructor(
    private readonly firebaseAppService: FirebaseAppService,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly authService: AuthService
  ) {}

  listenToNotifications(path: string): Observable<NotificationItem[]> {
    return new Observable<NotificationItem[]>((observer) => {
      if (!isPlatformBrowser(this.platformId)) {
        observer.next([]);
        observer.complete();
        return;
      }

      // Obtener el rol del usuario desde la sesión
      const currentUser = this.authService.currentUser;
      const userRole = currentUser?.role;

      if (!userRole) {
        if (typeof ngDevMode !== 'undefined' && ngDevMode) {
          console.warn(
            '[Firebase Realtime Database] No se pudo obtener el rol del usuario. No se escucharan notificaciones.'
          );
        }
        observer.next([]);
        observer.complete();
        return;
      }

      let unsubscribe: Unsubscribe | undefined;
      let cancelled = false;

      this.ensureDatabase()
        .then((database) => {
          if (!database || cancelled) {
            observer.next([]);
            observer.complete();
            return;
          }

          const dbRef = ref(database, path);
          const q = query(
            dbRef,
            orderByChild('recipient/role'),
            equalTo(userRole)
          );
          unsubscribe = onValue(
            q,
            (snapshot) => {
              const data = snapshot.val();
              const notifications = this.mapSnapshotToNotifications(data);

              this.zone.run(() => {
                observer.next(notifications);
              });
            },
            (error) => {
              console.error(
                '[Firebase Realtime Database] Error al escuchar notificaciones.',
                error
              );
              if (typeof ngDevMode !== 'undefined' && ngDevMode) {
                console.warn(
                  '[Firebase Realtime Database] Se devolvera una lista vacia mientras se recupera la conexion.'
                );
              }
              this.zone.run(() => observer.next([]));
            }
          );
        })
        .catch((error) => {
          console.error(
            '[Firebase Realtime Database] No se pudo inicializar la base de datos.',
            error
          );
          this.zone.run(() => {
            observer.next([]);
            observer.complete();
          });
        });

      return () => {
        cancelled = true;
        if (unsubscribe) {
          unsubscribe();
        }
      };
    });
  }

  listenToAuctionsRunning(path: string = 'auctions'): Observable<Record<string, any>> {
    return new Observable<Record<string, any>>((observer) => {
      if (!isPlatformBrowser(this.platformId)) {
        observer.next({});
        observer.complete();
        return;
      }

      let unsubscribe: Unsubscribe | undefined;
      let cancelled = false;

      this.ensureDatabase()
        .then((database) => {
          if (!database || cancelled) {
            observer.next({});
            observer.complete();
            return;
          }

          const dbRef = ref(database, path);
          // Filtrar por estado "running"; en la data puede llamarse lotState o status
          // Intentamos primero por lotState, si no, devolvemos todos y el consumidor puede filtrar
          const q = query(dbRef, orderByChild('lotState'), equalTo('running'));

          unsubscribe = onValue(
            q,
            (snapshot) => {
              const data = snapshot.val() as Record<string, any> | null;
              const value = data ?? {};
              this.zone.run(() => observer.next(value));
            },
            (error) => {
              console.error('[Firebase Realtime Database] Error al escuchar auctions running.', error);
              this.zone.run(() => observer.next({}));
            }
          );
        })
        .catch((error) => {
          console.error('[Firebase Realtime Database] No se pudo inicializar la base de datos.', error);
          this.zone.run(() => {
            observer.next({});
            observer.complete();
          });
        });

      return () => {
        cancelled = true;
        if (unsubscribe) unsubscribe();
      };
    });
  }

  private async ensureDatabase(): Promise<Database | null> {
    if (this.database) {
      return this.database;
    }

    const app = await this.firebaseAppService.getFirebaseApp();
    if (!app) {
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        console.warn(
          '[Firebase Realtime Database] FirebaseApp no esta inicializado.'
        );
      }
      return null;
    }

    this.database = getDatabase(app);
    return this.database;
  }

  private mapSnapshotToNotifications(value: unknown): NotificationItem[] {
    const entries = this.normalizeToEntries(value);
    return entries
      .map(({ key, data }) => {
        if (!data || typeof data !== 'object') return null;
        const n = data as Omit<NotificationItem, 'id'> & Partial<Pick<NotificationItem, 'id'>>;
        return { id: key, ...n } as NotificationItem;
      })
      .filter((notification): notification is NotificationItem => notification !== null)
      .sort((a, b) => b.data.timestamp - a.data.timestamp);
  }

  private normalizeToEntries(
    value: unknown
  ): Array<{ key: string; data: unknown }> {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .map((data, index) => ({ key: String(index), data }))
        .filter((entry) => entry.data != null);
    }

    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).map(
        ([key, data]) => ({
          key,
          data,
        })
      );
    }

    return [];
  }
}
